import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, MoveHorizontal } from 'lucide-react'
import { wavePlanPath } from './routes'
import { WavePlanStatus } from './WavePlanStatus'
import { WaveRollupPlanDetails } from './WaveRollupPlanDetails'
import { ColumnResizer } from './ColumnResizer'
import { useColumnSizing, type SizedColumn } from './columnSizing'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { WaveWeek } from './model'
import {
  formatRollupDate,
  formatRollupValue,
  type RollupChartMetric,
  type RollupGroup,
  type RollupHighlight,
  type RollupMetric,
} from './rollup-model'

const metrics: { key: RollupMetric; label: string }[] = [
  { key: 'percent', label: 'Wave %' },
  { key: 'units', label: 'Wave Units' },
  { key: 'skuCount', label: 'Wave SKU Count' },
  { key: 'repackSkuCount', label: 'Wave Repack SKU Count' },
  { key: 'cube', label: 'Wave Cube (ft³)' },
]

export type RollupTableTarget = RollupHighlight & {
  metric: RollupChartMetric
  focus: boolean
  requestId: number
}

export function WaveRollupTable({
  group,
  weeks,
  expanded,
  onToggle,
  highlight,
  target,
  active,
}: {
  group: RollupGroup
  weeks: WaveWeek[]
  expanded: Set<string>
  onToggle: (planId: string) => void
  highlight: RollupHighlight | null
  target: RollupTableTarget | null
  active: boolean
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const narrow = useMediaQuery('(max-width: 639px)')
  // Resizable columns; the waves share any extra width.
  const columns = useMemo<SizedColumn[]>(
    () => [
      { key: 'plan', width: narrow ? 126 : 242, minWidth: 110 },
      { key: 'metric', width: narrow ? 70 : 152, minWidth: 64 },
      ...weeks.map(week => ({
        key: `week-${week.week}`,
        width: narrow ? 100 : 108,
        minWidth: 72,
      })),
      { key: 'season', width: 120, minWidth: 90 },
    ],
    [weeks, narrow]
  )
  const fill = useMemo(() => weeks.map(week => `week-${week.week}`), [weeks])
  const sizing = useColumnSizing(columns, fill)
  const resizer = (key: string, label: string) => (
    <ColumnResizer
      label={label}
      width={sizing.widths[key]}
      onResize={width => sizing.resize(key, width)}
      onReset={() => sizing.reset(key)}
    />
  )
  const position = useRef({ left: 0, top: 0 })
  const revealedRequest = useRef<number>()
  const [overflow, setOverflow] = useState({ left: false, right: false })
  const updateOverflow = () => {
    const element = scrollRef.current
    if (!element || !element.clientWidth) return
    setOverflow({
      left: element.scrollLeft > 1,
      right: element.scrollWidth - element.clientWidth - element.scrollLeft > 1,
    })
  }
  useEffect(() => {
    const observer = new ResizeObserver(updateOverflow)
    if (scrollRef.current) observer.observe(scrollRef.current)
    if (tableRef.current) observer.observe(tableRef.current)
    return () => observer.disconnect()
  }, [])
  useLayoutEffect(() => {
    if (active && scrollRef.current) {
      scrollRef.current.scrollTo(position.current.left, position.current.top)
      updateOverflow()
    }
  }, [active])
  useLayoutEffect(() => {
    if (!active || !target || target.requestId === revealedRequest.current)
      return
    const scroll = scrollRef.current
    const cell = tableRef.current?.querySelector<HTMLTableCellElement>(
      'tbody[data-plan-id="' +
        CSS.escape(target.planId) +
        '"] [data-metric="' +
        target.metric +
        '"] [data-wave="' +
        target.week +
        '"]'
    )
    if (!scroll || !cell) return
    revealedRequest.current = target.requestId
    // On narrow/short screens the page owns vertical scrolling.
    if (
      !window.matchMedia('(min-width: 1024px) and (min-height: 700px)').matches
    ) {
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
    const bounds = scroll.getBoundingClientRect()
    const cellBounds = cell.getBoundingClientRect()
    const fixedWidth =
      (tableRef.current
        ?.querySelector('.rollup-plan-cell')
        ?.getBoundingClientRect().width ?? 0) +
      (tableRef.current
        ?.querySelector('.rollup-metric-cell')
        ?.getBoundingClientRect().width ?? 0)
    const headerBottom =
      tableRef.current?.tHead?.getBoundingClientRect().bottom ?? bounds.top
    const left =
      cellBounds.left < bounds.left + fixedWidth
        ? cellBounds.left - bounds.left - fixedWidth - 4
        : Math.max(0, cellBounds.right - bounds.right + 4)
    const top =
      cellBounds.top < headerBottom
        ? cellBounds.top - headerBottom - 4
        : Math.max(0, cellBounds.bottom - bounds.bottom + 4)
    scroll.scrollBy({ left, top, behavior: 'instant' })
    position.current = { left: scroll.scrollLeft, top: scroll.scrollTop }
    if (target.focus) cell.focus({ preventScroll: true })
    updateOverflow()
  }, [target, active, expanded])

  return (
    <div className="rollup-table-area">
      {(overflow.left || overflow.right) && (
        <div className="rollup-scroll-cue" data-testid="rollup-scroll-hint">
          <MoveHorizontal size={14} />
          {overflow.left && overflow.right
            ? 'More waves on both sides'
            : overflow.right
              ? 'Scroll right for more waves'
              : 'Scroll left for more waves'}
        </div>
      )}
      <div
        ref={element => {
          scrollRef.current = element
          sizing.containerRef.current = element
        }}
        className="rollup-table-scroll"
        tabIndex={0}
        role="region"
        aria-label={group.calendar.label + ' rollup table'}
        onScroll={() => {
          if (!active || !scrollRef.current?.clientWidth) return
          position.current = {
            left: scrollRef.current.scrollLeft,
            top: scrollRef.current.scrollTop,
          }
          updateOverflow()
        }}
      >
        <table
          ref={tableRef}
          className="rollup-table"
          style={
            {
              width: sizing.tableWidth,
              // The sticky metric column sits right of the plan column.
              '--rollup-plan-width': `${sizing.widths.plan}px`,
              '--rollup-metric-width': `${sizing.widths.metric}px`,
            } as CSSProperties
          }
        >
          <colgroup>
            {columns.map(column => (
              <col
                key={column.key}
                data-column={column.key}
                style={{ width: sizing.widths[column.key] }}
              />
            ))}
          </colgroup>
          <caption className="sr-only">
            {group.calendar.label} wave rollup. Expand plans to inspect all five
            metrics. Use Details for plan information.
          </caption>
          <thead>
            <tr>
              <th className="rollup-plan-cell" scope="col">
                Plan / Status
                {resizer('plan', 'Plan / Status')}
              </th>
              <th className="rollup-metric-cell" scope="col">
                Metric
                {resizer('metric', 'Metric')}
              </th>
              {weeks.map(week => (
                <th
                  key={week.week}
                  scope="col"
                  className={
                    highlight?.week === week.week
                      ? 'rollup-highlight-column'
                      : ''
                  }
                >
                  <span className="block">{week.event}</span>
                  <span className="mt-1 block text-xs font-normal text-muted-foreground">
                    {formatRollupDate(week.date)}
                  </span>
                  {resizer(`week-${week.week}`, week.event)}
                </th>
              ))}
              <th className="rollup-season" scope="col">
                TTL Season
                {resizer('season', 'TTL Season')}
              </th>
            </tr>
          </thead>
          {group.plans.map(row => {
            const isExpanded = expanded.has(row.plan.id)
            const visibleMetrics = isExpanded ? metrics : metrics.slice(0, 1)
            const highlighted = highlight?.planId === row.plan.id
            return (
              <tbody
                key={row.plan.id}
                data-plan-id={row.plan.id}
                className={highlighted ? 'rollup-highlight-plan' : ''}
              >
                {visibleMetrics.map((metric, index) => (
                  <tr key={metric.key} data-metric={metric.key}>
                    {index === 0 && (
                      <th
                        rowSpan={visibleMetrics.length}
                        scope="rowgroup"
                        className="rollup-plan-cell"
                      >
                        <div className="flex items-start gap-1.5">
                          <button
                            type="button"
                            className="rollup-expand"
                            onClick={() => onToggle(row.plan.id)}
                            aria-expanded={isExpanded}
                            aria-label={
                              (isExpanded ? 'Collapse ' : 'Expand ') +
                              row.plan.name
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            <Link
                              className="font-semibold hover:text-primary hover:underline focus-visible:outline focus-visible:outline-2"
                              to={wavePlanPath(row.plan.id)}
                            >
                              {row.plan.name}
                            </Link>
                            <div className="rollup-plan-context">
                              {row.details.hierarchyCode && (
                                <span>{row.details.hierarchyCode}</span>
                              )}
                              <span>Set {formatRollupDate(row.setDate)}</span>
                            </div>
                            <div className="rollup-plan-actions">
                              <WavePlanStatus status={row.plan.status} />
                              <WaveRollupPlanDetails row={row} />
                            </div>
                            {row.needsReview && (
                              <p className="mt-1 text-xs font-medium text-destructive">
                                Review allocations
                              </p>
                            )}
                          </div>
                        </div>
                      </th>
                    )}
                    <th scope="row" className="rollup-metric-cell">
                      {metric.label}
                    </th>
                    {weeks.map(week => (
                      <td
                        key={week.week}
                        data-wave={week.week}
                        tabIndex={-1}
                        aria-label={
                          row.plan.name +
                          ', ' +
                          week.event +
                          ', ' +
                          metric.label +
                          ': ' +
                          formatRollupValue(
                            row.waves[week.week][metric.key],
                            metric.key
                          )
                        }
                        className={
                          highlight?.week === week.week
                            ? 'rollup-highlight-column'
                            : ''
                        }
                      >
                        <span
                          className={
                            highlighted && highlight?.week === week.week
                              ? 'rollup-highlight-value'
                              : ''
                          }
                        >
                          {formatRollupValue(
                            row.waves[week.week][metric.key],
                            metric.key
                          )}
                        </span>
                      </td>
                    ))}
                    <td className="rollup-season" data-season>
                      {formatRollupValue(row.season[metric.key], metric.key)}
                    </td>
                  </tr>
                ))}
              </tbody>
            )
          })}
          <tfoot>
            {metrics
              .filter(metric =>
                ['percent', 'units', 'cube'].includes(metric.key)
              )
              .map((metric, index) => (
                <tr key={metric.key} data-total-metric={metric.key}>
                  {index === 0 && (
                    <th
                      rowSpan={3}
                      scope="rowgroup"
                      className="rollup-plan-cell"
                    >
                      Calendar total
                      <p className="mt-1 text-xs font-normal text-muted-foreground">
                        {group.plans.length}{' '}
                        {group.plans.length === 1 ? 'plan' : 'plans'}
                      </p>
                    </th>
                  )}
                  <th scope="row" className="rollup-metric-cell">
                    {metric.label}
                  </th>
                  {weeks.map(week => (
                    <td key={week.week} data-wave={week.week}>
                      {formatRollupValue(
                        group.waves[week.week][metric.key],
                        metric.key
                      )}
                    </td>
                  ))}
                  <td className="rollup-season" data-season>
                    {formatRollupValue(group.season[metric.key], metric.key)}
                  </td>
                </tr>
              ))}
          </tfoot>
        </table>
      </div>
      <div className="rollup-table-note">
        <span>
          SKU counts aren’t additive across waves. Cube includes all DCs.
        </span>
      </div>
    </div>
  )
}
